import { useState } from 'react';

export interface UseExpandingOverlayResult {
  /** Whether the overlay should be mounted (open, or animating closed). */
  isOpen: boolean;
  /** Open the overlay, growing out of the given on-screen rect. */
  expand: (origin: DOMRect) => void;
  /** Request close — plays the shrink animation, then unmounts. */
  requestClose: () => void;
  /** Props to spread onto `<ExperiencePageLayout>`. */
  layoutProps: {
    overlayOrigin: DOMRect | null;
    overlayOpen: boolean;
    onOverlayExited: () => void;
  };
}

/**
 * Drives the expand-on-play overlay lifecycle for the experience page:
 * mount → grow out of a thumbnail rect → shrink back → unmount. It keeps
 * the three coordinated pieces of state (mounted / closing / origin rect)
 * in one place so a page only wires its data and the play/close triggers:
 *
 * ```tsx
 * const overlay = useExpandingOverlay();
 * // poster:  onPlay={overlay.expand}
 * // player:  onClose={overlay.requestClose}
 * // layout:  sidebarOverlay={overlay.isOpen && url ? <Player … /> : undefined}
 * //          {...overlay.layoutProps}
 * ```
 */
export function useExpandingOverlay(): UseExpandingOverlayResult {
  const [isOpen, setIsOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [origin, setOrigin] = useState<DOMRect | null>(null);

  return {
    isOpen,
    expand: (nextOrigin) => {
      setOrigin(nextOrigin);
      setClosing(false);
      setIsOpen(true);
    },
    requestClose: () => setClosing(true),
    layoutProps: {
      overlayOrigin: origin,
      // While closing, overlayOpen is false so ExpandingOverlay plays the
      // shrink; the overlay stays mounted until onOverlayExited fires.
      overlayOpen: !closing,
      onOverlayExited: () => {
        setIsOpen(false);
        setClosing(false);
      },
    },
  };
}
